import React, { useRef, useContext, useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import {
  selectTimerById,
  selectTimerIsShowingOptions
} from 'selectors/timerSelectors'
import TimerContext from './TimerContext'
import TimerProgressIndicator from './TimerProgressIndicator.jsx'
import TimerOptions from './TimerOptions.jsx'
import Store from '../../../store'
import * as modalActions from 'actions/modalActions'
import HotTextDialog from '../hotTextDialog/HotTextDialog.jsx'
import * as apiActions from 'actions/apiActions'
import { getDefaultCallbacks } from 'Helper/apiHelper'
import StandardModalContent from '../StandardModalContent/StandardModalContent.jsx'
import userHasPrivs from 'Helper/Auth/userHasPrivs'

const Timer = React.memo(props => {
  const logEntry = useRef('')
  const [logDialogOpen, setLogDialogOpen] = useState(false)
  const containerRef = useRef(null)
  const timerContext = useContext(TimerContext)
  const instance = useSelector(state => selectTimerById(state, props.timerId))
  const isShowingOptions = useSelector(state =>
    selectTimerIsShowingOptions(state, timerContext.view, props.timerId)
  )
  const dispatch = useDispatch()
  const updateVialTestPriv = userHasPrivs('vial_test_scoring_create')
  const updateSkinTestPriv = userHasPrivs('st_details_update')

  useEffect(() => {
    const handleOutsideClick = e => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        dispatch({ type: 'TIMER_OPTIONS_HIDE' })
      }
    }

    if (isShowingOptions && !logDialogOpen) {
      document.addEventListener('mousedown', handleOutsideClick)
    }
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
    }
  }, [isShowingOptions, logDialogOpen])

  const updateSkinTestLog = message => {
    const { patient_id, skintest_id } = instance.payload
    const data = {
      test_log_note: { content: message }
    }

    dispatch(
      apiActions.xtractAPI(
        `patient/${patient_id}/skintest/${skintest_id}`,
        'update',
        data,
        {
          ...getDefaultCallbacks(() => {}),
          200: response => {}
        }
      )
    )
  }

  const handleDeleteClick = e => {
    if (instance.payload.skintest_id) {
      if (!updateSkinTestPriv) {
        dispatch(
          modalActions.openModal('no_privs', {
            title: 'Missing Privilege',
            content: (
              <StandardModalContent
                messageContent={
                  <div style={{ marginBottom: '25px' }}>
                    The Update Skintest Details privilege within Skintest Details is required
                    to perform this action
                  </div>
                }
                okHandler={() => null}
                okText={'OK'}
                okClassName={'btn-success'}
              />
            ),
            closeable: true
          })
        )
        return
      }
      setLogDialogOpen(true)
      logEntry.current = ''
      Store.dispatch(
        modalActions.openModal('skintest_log_modal', {
          title: 'Stop Skintest Timer',
          content: (
            <HotTextDialog
              hotTextName={'skintest-timer-stop'}
              value={''}
              onChange={value => {
                logEntry.current = value
              }}
              cancelHandler={() => {
                setLogDialogOpen(false)
              }}
              setFinalValue={() => {
                setLogDialogOpen(false)
                dispatch(
                  apiActions.xtractAPI(
                    `timer/${props.timerId}`,
                    'delete',
                    null,
                    {
                      200: () => {
                        updateSkinTestLog(
                          'timer stop' +
                            (logEntry.current ? ' - ' + logEntry.current : '')
                        )
                      }
                    }
                  )
                )
              }}
              type={'text'}
              closeSelf={() => {
                Store.dispatch(modalActions.closeModal('skintest_log_modal'))
              }}
            />
          ),
          closeable: true
        })
      )
    } else {
      if (instance.type === 'vialTest' && !updateVialTestPriv) {
        dispatch(
          modalActions.openModal('no_privs', {
            title: 'Missing Privilege',
            content: (
              <StandardModalContent
                messageContent={
                  <div style={{ marginBottom: '25px' }}>
                    The Create Vial Test privilege within Vial Test is required
                    to perform this action
                  </div>
                }
                okHandler={() => null}
                okText={'OK'}
                okClassName={'btn-success'}
              />
            ),
            closeable: true
          })
        )
      } else {
        dispatch({ type: 'TIMER_OPTIONS_HIDE' })
        dispatch(
          modalActions.openModal('delete timer', {
            title: 'Confirm Stop',
            content: (
              <StandardModalContent
                messageContent={
                  <div style={{ marginBottom: '25px' }}>
                    Are you sure you want to stop this timer?
                  </div>
                }
                okHandler={() => {
                  dispatch(
                    apiActions.xtractAPI(
                      `timer/${props.timerId}`,
                      'delete',
                      null,
                      {
                        200: () => {}
                      }
                    )
                  )
                }}
                okText={'Stop'}
                okClassName={'btn-danger'}
                cancelHandler={() => {}}
                cancelText={'Cancel'}
                cancelClassName={'btn-success'}
              />
            ),
            closeable: true
          })
        )
      }
    }
  }

  const handleClick = e => {
    e.stopPropagation()
    dispatch({
      type: 'TIMER_OPTIONS_SHOW',
      timerContext,
      timer_id: !isShowingOptions ? props.timerId : null
    })
  }

  if (!instance) {
    return null
  }

  let timerClassName = `timer-timer timerContextView-${timerContext.view}`
  let timerGraphicClassName = 'timer-timer-graphic'
  let containerClassName = 'timer-timer-container'

  if (instance.isAlarming) {
    timerClassName += ' alarming'
  } else if (instance.isAlmostAlarming) {
    timerClassName += ' almost-alarming'
  }

  if (props.hideTime && !isShowingOptions) {
    timerGraphicClassName += ' no-text'
    timerClassName += ' no-text'
  }

  if (isShowingOptions) {
    containerClassName += ' open'
  }

  return (
    <div ref={containerRef} className={containerClassName}>
      <div className={timerClassName} onClick={handleClick}>
        <div className={timerGraphicClassName}>
          <TimerProgressIndicator
            R={props.indicatorR || 13.5}
            progress={instance.progress}
          />
        </div>
        {props.hideTime && !isShowingOptions
          ? null
          : <div className="timer-timer-labels">
            <div className="timer-timer-title">{instance.title}</div>
            <div className="timer-timer-time">{instance.formattedTime}</div>
          </div>
        }
      </div>
      {isShowingOptions
        ? <TimerOptions
          timerId={props.timerId}
          handleDeleteClick={handleDeleteClick}
        />
        : null
      }
    </div>
  )
})

export default Timer
